import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  LayoutTemplate,
  ListChecks,
  Plus,
  Sigma,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  Spinner,
} from '../../ui';
import { useTemplates } from '../../features/templates/useTemplates';

/**
 * Templates list — grouped cards, one per template name showing its latest
 * version. Reads through the useTemplates hook only. Clicking a card opens the
 * latest version in the builder.
 */
export function TemplatesPage() {
  const navigate = useNavigate();
  const { groups, loading, error, ensureDefaults } = useTemplates();

  // Idempotently ensure the built-in default templates exist for every user
  // (not just first-run workspaces). Only writes when a default is absent.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    void ensureDefaults();
  }, [ensureDefaults]);

  const newButton = (
    <Button
      leftIcon={<Plus className="h-4 w-4" />}
      onClick={() => navigate('/templates/new')}
    >
      New Template
    </Button>
  );

  return (
    <div>
      <PageHeader
        icon={<LayoutTemplate className="h-5 w-5" />}
        title="Templates"
        description="Define arbitrary, fully-configurable schemas that power estimation and work tracking."
        actions={newButton}
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading templates" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load templates"
          description={error}
        />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<LayoutTemplate className="h-6 w-6" />}
          title="No templates yet"
          description="Templates describe the fields, factors, and estimation outputs that rooms and estimates are built from."
          action={newButton}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {groups.map((group) => {
            const t = group.latest;
            return (
              <Card
                key={group.name}
                interactive
                onClick={() =>
                  navigate(`/templates/${encodeURIComponent(t.id)}`)
                }
              >
                <CardHeader>
                  <CardTitle>{group.name}</CardTitle>
                  <Badge tone="accent">v{t.version}</Badge>
                </CardHeader>
                <CardBody className="space-y-4">
                  <p className="line-clamp-2 min-h-[2.5rem] text-sm text-zinc-400">
                    {t.description || 'No description provided.'}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <Badge tone="neutral">
                      <ListChecks className="mr-1 h-3 w-3" />
                      {t.fieldCount} fields
                    </Badge>
                    {group.versions.length > 1 && (
                      <Badge tone="info">
                        <Sigma className="mr-1 h-3 w-3" />
                        {group.versions.length} versions
                      </Badge>
                    )}
                    {t.createdBy && <span>by {t.createdBy}</span>}
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TemplatesPage;
